/**
 * Cognito Hosted UI auth (Authorization Code + PKCE) for the SPA. Works with native
 * Cognito users today and, once Entra federation is enabled, the same flow gains a
 * "Sign in with Versent" button on the Hosted UI — no SPA change.
 *
 * The verified ID token is sent as the API bearer (the JWT authorizer validates ID
 * tokens). If/when we move to the documented Lambda@Edge cookie model, only this module
 * + the token-attach in api/client.ts change.
 */
import { setAuthToken } from '../api/client';

const DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN;
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID;
// When set (e.g. "VersentIdP"), go straight to that federated IdP and skip the Hosted UI's
// IdP-chooser / native email+password page. Truthy check: CI may bake this as "" when unset.
const IDP_NAME = import.meta.env.VITE_IDP_NAME;
// Use `||` not `??`: CI bakes VITE_REDIRECT_URI as an EMPTY STRING when the repo var is
// unset (it still passes `VITE_REDIRECT_URI: ${{ vars.VITE_REDIRECT_URI }}`), and `??`
// would keep the empty string — sending an empty redirect_uri → Cognito `redirect_mismatch`.
const REDIRECT_URI =
  import.meta.env.VITE_REDIRECT_URI || `${window.location.origin}/auth/callback`;

/** Auth is wired only when the Cognito build vars are present (live builds). */
export const authConfigured = Boolean(DOMAIN && CLIENT_ID);

const TOKEN_KEY = 'fluentai_id_token';
const VERIFIER_KEY = 'fluentai_pkce_verifier';
const SIGNED_OUT_KEY = 'fluentai_signed_out';

export function isSignedOut(): boolean {
  return sessionStorage.getItem(SIGNED_OUT_KEY) === 'true';
}

// ---- PKCE helpers ----
function base64url(bytes: Uint8Array): string {
  let s = '';
  bytes.forEach((b) => (s += String.fromCharCode(b)));
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomVerifier(): string {
  const a = new Uint8Array(32);
  crypto.getRandomValues(a);
  return base64url(a);
}

async function challenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

// ---- token helpers ----
interface Claims {
  exp: number;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  'cognito:username'?: string;
  'custom:role'?: string;
  'cognito:groups'?: string[];
}

function decode(idToken: string): Claims | undefined {
  try {
    const payload = idToken.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return undefined;
  }
}

function isExpired(idToken: string): boolean {
  const c = decode(idToken);
  return !c || c.exp * 1000 <= Date.now();
}

function persist(idToken: string): void {
  sessionStorage.removeItem(SIGNED_OUT_KEY);
  sessionStorage.setItem(TOKEN_KEY, idToken);
  setAuthToken(idToken);
}

export function getIdToken(): string | undefined {
  return sessionStorage.getItem(TOKEN_KEY) ?? undefined;
}

export function isAuthed(): boolean {
  const t = getIdToken();
  return Boolean(t && !isExpired(t));
}

export function currentUser(): { name?: string; email?: string } | undefined {
  const t = getIdToken();
  if (!t) return undefined;
  const c = decode(t);
  if (!c) return undefined;
  // Cognito's username can be an opaque UUID, not a person's display name.
  const fullName = typeof c.name === 'string' ? c.name.trim() : '';
  const parts = [c.given_name, c.family_name]
    .filter((part): part is string => typeof part === 'string')
    .map((part) => part.trim()).filter(Boolean).join(' ');
  const isIdentifier = (value: string) => /^(?:.*_)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
  const name = [fullName, parts].find((value) => value && !isIdentifier(value));
  return { name, email: typeof c.email === 'string' ? c.email.trim() || undefined : undefined };
}

/** The participant's role from the IdP claim, if present (empty unless map_org_claims is on). */
export function currentRole(): string | undefined {
  const t = getIdToken();
  if (!t) return undefined;
  const r = decode(t)?.['custom:role'];
  return r && r.trim() ? r.trim() : undefined;
}

// Exact Cognito group that grants programme-admin access. Must match the backend
// ADMIN_GROUP and the Cognito group provisioned in Terraform.
const ADMIN_GROUP = import.meta.env.VITE_ADMIN_GROUP || 'programme-admin';

/**
 * Programme-admin check, mirroring the backend gate (admin.ts): exact membership of
 * the configured Cognito group via the `cognito:groups` claim. This only gates the
 * UI; the backend independently enforces the same group check on every admin request.
 */
export function isProgrammeAdmin(): boolean {
  const t = getIdToken();
  // Require a live (non-expired) token, mirroring isAuthed — otherwise an expired session still
  // renders the admin surfaces, whose API calls then fail against the authorizer.
  if (!t || isExpired(t)) return false;
  const c = decode(t);
  if (!c) return false;
  return Array.isArray(c['cognito:groups']) && c['cognito:groups'].includes(ADMIN_GROUP);
}

/** Re-attach a still-valid token on app boot (e.g. a refresh). */
export function restoreSession(): boolean {
  const t = getIdToken();
  if (t && !isExpired(t)) {
    setAuthToken(t);
    return true;
  }
  sessionStorage.removeItem(TOKEN_KEY);
  return false;
}

/** Redirect to the Hosted UI to sign in. */
export async function login(): Promise<void> {
  if (!authConfigured) throw new Error('Auth not configured');
  const verifier = randomVerifier();
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  const params = new URLSearchParams({
    client_id: CLIENT_ID!,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: REDIRECT_URI,
    code_challenge_method: 'S256',
    code_challenge: await challenge(verifier),
  });
  // Skip the Hosted UI chooser and federate straight to the corporate IdP when configured.
  if (IDP_NAME) params.set('identity_provider', IDP_NAME);
  window.location.assign(`https://${DOMAIN}/oauth2/authorize?${params}`);
}

/** Handle the /auth/callback redirect: exchange the code for tokens. */
export async function completeLogin(): Promise<boolean> {
  const code = new URLSearchParams(window.location.search).get('code');
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (!code || !verifier) return false;
  const res = await fetch(`https://${DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: CLIENT_ID!,
      code,
      redirect_uri: REDIRECT_URI,
      code_verifier: verifier,
    }),
  });
  sessionStorage.removeItem(VERIFIER_KEY);
  if (!res.ok) return false;
  const tokens = (await res.json()) as { id_token?: string };
  if (!tokens.id_token) return false;
  persist(tokens.id_token);
  window.history.replaceState({}, '', '/'); // strip ?code from the URL
  return true;
}

export function logout(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.setItem(SIGNED_OUT_KEY, 'true');
  setAuthToken('');
  if (authConfigured) {
    const params = new URLSearchParams({
      client_id: CLIENT_ID!,
      logout_uri: `${window.location.origin}/`,
    });
    window.location.assign(`https://${DOMAIN}/logout?${params}`);
  }
}
